import { useCallback } from 'react'
import type { Channel, ChannelSort, StreamChat } from 'stream-chat'

import { useChatRuntimeConfig } from '../../runtime/chat-runtime-config'
import type { IChatCurrentUser } from '../../runtime/types'
import type { IChatUser } from '../../types/chat-types'
import { isEmail, isPhone } from '../../utils/common'

/**
 * Hook that provides user search functionality via Stream Chat channels.
 * Queries messaging channels the current user belongs to and filters members
 * by name, email, or phone number.
 */
export function useChatUserSearch(chatClient: StreamChat, userDetails: IChatCurrentUser | null | undefined) {
  const { telemetry } = useChatRuntimeConfig()
  const queryAndFilterUsers = useCallback(
    async (inputValue: string, isRetry: boolean = false): Promise<IChatUser[]> => {
      if (!userDetails?.id) return []

      const filters = {
        members: { $in: [userDetails.id] },
        type: 'messaging'
      }
      const sort: ChannelSort = { last_message_at: -1 as const }
      const options = { limit: 30 }

      let channels: Channel[]
      try {
        channels = await chatClient.queryChannels(filters, sort, options)
      } catch (error) {
        const isNetworkError = error instanceof Error && error.message.includes('ERR_NETWORK_CHANGED')

        if (isNetworkError && !isRetry) {
          telemetry.addBreadcrumb?.({
            category: 'chat',
            level: 'warning',
            message: 'Network changed detected, attempting retry'
          })
          await new Promise((resolve) => setTimeout(resolve, 500))
          return queryAndFilterUsers(inputValue, true)
        }

        telemetry.captureException?.(error, {
          tags: { context: isRetry ? 'searchUser method retry' : 'searchUser method' },
          extra: { filters, inputValue, options, sort, userId: userDetails?.id }
        })
        return []
      }

      const lowercasedInput = inputValue.toLowerCase()
      const isEmailSearch = isEmail(inputValue)
      const isPhoneSearch = isPhone(inputValue)

      return channels.flatMap((channel: Channel) => {
        const connectedUserID = Object.keys(channel.state.members).find((key) => key !== userDetails.id)
        if (!connectedUserID) return []

        const userData = channel.state.members[connectedUserID].user as IChatUser
        if (userData?.deleted) return []

        if (isEmailSearch && userData?.email === inputValue) return [userData]
        if (isPhoneSearch && userData?.phone === inputValue) return [userData]
        if (userData?.name?.toLowerCase().includes(lowercasedInput)) return [userData]

        return []
      })
    },
    [chatClient, telemetry, userDetails?.id]
  )

  const searchUser = useCallback(
    async (inputValue: string): Promise<IChatUser[]> => {
      if (!inputValue.trim()) return []
      return queryAndFilterUsers(inputValue, false)
    },
    [queryAndFilterUsers]
  )

  return { searchUser }
}
